import { Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';
import { take } from 'rxjs';
import { WorkspaceService } from '../workspace.service';
import { GlobalService } from '../../global/services/global.service';
import { AppService } from '../../app.service';
import { GridEventService } from '../../core/grid/services/grid-event.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { ImportUploadDialogComponent } from './import-upload-dialog.component';
import { ImportPasteDialogComponent } from './import-paste-dialog.component';
import { BatchProcessListDialogComponent } from './batch-process-list-dialog.component';
import { ScreenDocumentsComponent } from '../../screen-docs/screen-documents.component';
import { environment } from '../../../environments/environment';
import { ConfirmationModalConfig } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { aoaToXlsxFile } from '../../shared/_utils/excel-workbook.util';

/** Must stay in sync with `GridDirective.IMPORT_GRID_SCREEN_TYPES` and `data-filters`. */
const IMPORT_SCREEN_TYPES: ReadonlySet<string> = new Set([
  'grid-import',
  'grid-import-td',
  'grid-import-pivot',
  'grid-import-validation',
]);

function isImportScreenType(screenType: any): boolean {
  return IMPORT_SCREEN_TYPES.has(String(screenType ?? '').trim().toLowerCase());
}

@Component({
    selector: 'gtw-grid-import-toolbar',
    templateUrl: './grid-import-toolbar.component.html',
    styleUrls: ['./grid-import-toolbar.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GridImportToolbarComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  isImportScreen = false;
  screenType = '';
  importObj: any = {};
  showSaveBtn = false;
  showUploadBtn = true;
  batchTags: string[] = [];
  tagNumber = '';
  feedFile = '';
  transFile = '';
  /** Cached from upload / batch load — used to enrich paste rows before save. */
  private importRowEnrichmentTemplate: any | null = null;
  showCancelConfirmModal = false;
  cancelConfirmConfig: ConfirmationModalConfig = {
    title: 'Clear Batch Import',
    message: 'Are you sure you want to clear the data you entered? All changes will be lost.',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
  };

  private tabSub!: Subscription;
  private activeScreen: any;
  private showSaveButtonListenerFn: any = null;

  /**
   * Legacy parity (`DataFiltersController.js:777-783`, `:976-979`): insurance
   * screen detection ran with `==` (loose equality) so both `'237'` and `237`
   * matched. The migrated `.includes` uses strict comparison — so we stringify
   * the key/id before lookup to survive number-vs-string drift coming from
   * `globalParams.screen_key` and `activeScreen.id`. Stored as `Set<string>`
   * for O(1) membership instead of repeated array scans.
   */
  private static readonly INSURANCE_SCREEN_KEYS: ReadonlySet<string> = new Set(
    ['237', '11471', '11287', '11294', '11324', '11327']
  );
  private static readonly INSURANCE_CANCEL_IDS: ReadonlySet<string> = new Set(
    ['237', '11471', '11287', '11294', '11324', '11327']
  );

  private static isInsuranceScreen(screenKey: any): boolean {
    if (screenKey == null) return false;
    return GridImportToolbarComponent.INSURANCE_SCREEN_KEYS.has(String(screenKey).trim());
  }

  private static isInsuranceCancelId(id: any): boolean {
    if (id == null) return false;
    return GridImportToolbarComponent.INSURANCE_CANCEL_IDS.has(String(id).trim());
  }

  /**
   * Group-25 import screens (K2/K3 "Import Other Part & Sections") gate the
   * Save button behind the fk-32 single-select. Detect them from the screen's
   * loaded filter definitions (filter keys 30/31/32) so Save is not
   * force-enabled on tab activation before a Form / Schedule selection.
   */
  private static isGroup25GatedScreen(ws: any): boolean {
    const filters: any[] =
      ws?.filters?.getFilters?.() || ws?.filters?.filters || [];
    if (!Array.isArray(filters)) return false;
    return filters.some((f: any) => [30, 31, 32].includes(Number(f?.filter_key)));
  }

  constructor(
    private workspaceService: WorkspaceService,
    private globalService: GlobalService,
    private appService: AppService,
    private gridEventService: GridEventService,
    private gtwSnackbarService: GtwSnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.tabSub = this.workspaceService.setTabActive.subscribe((ws: any) => {
      if (!ws?.screen_key) return;
      this.activeScreen = ws;
      this.screenType = ws.screen_type || '';

      // Screen 11471 (insurance-sch-p-feed-data) is a raw-data display-only
      // grid — it has an import screen_type so the grid renders correctly, but
      // the import toolbar must never appear on it (the data arrives via the
      // feed-upload toolbar on a sibling screen, not through this toolbar).
      if (String(ws.screen_key) === '11471') {
        this.isImportScreen = false;
        return;
      }

      this.isImportScreen = isImportScreenType(this.screenType);

      if (!this.isImportScreen) return;

      this.showUploadBtn = true;

      if (GridImportToolbarComponent.isInsuranceScreen(
        this.globalService.global?.globalParams?.screen_key
      )) {
        this.showSaveBtn = this.globalService.getInsuranceSaveBtnState();
      } else if (GridImportToolbarComponent.isGroup25GatedScreen(ws)) {
        // K2/K3 "Import Other Part & Sections" (group-25, filter keys 30/31/32)
        // gates Save behind the fk-32 single-select. Honor the persisted gate
        // here instead of force-enabling; `data-filters` re-broadcasts the
        // correct state via `broadcastImportSaveGate` when the bar is revealed.
        this.showSaveBtn = this.globalService.getInsuranceSaveBtnState();
      } else {
        this.showSaveBtn = false;
      }

      this.initImportScreen();
      this.cdr.detectChanges();
    });

    // Legacy `DataFiltersController.js:1022-1025` listened to `showSaveButton`
    // broadcast by the `dfSelect` directive and mirrored the boolean into
    // `vm.showSaveBtn` + `GlobalService.setInsuranceSaveBtnState`. The
    // migrated broadcast now originates in `data-filters.component.ts`
    // (`maybeUpdateImportSaveButtonState`); this listener closes the loop.
    this.showSaveButtonListenerFn = this.gridEventService.on(
      'showSaveButton',
      'gridImportToolbar',
      (args: any) => {
        if (typeof args?.showBtn === 'boolean') {
          this.showSaveBtn = args.showBtn;
          this.globalService.setInsuranceSaveBtnState(args.showBtn);
          this.cdr.detectChanges();
        }
      }
    );
  }

  private initImportScreen(): void {
    if (typeof this.activeScreen.importScreen === 'undefined') {
      this.activeScreen.importScreen = {
        step: 1,
        dirty: false,
        submitted: false,
        times_submitted: 0,
        job_number: null,
        saving: false,
        records: 0,
        cancelcrudLoading: false,
        crudLoading: false,
        errorLength: null,
      };
    }
    this.importObj = this.activeScreen.importScreen;
    this.batchTags = [];
    this.tagNumber = '';
  }

  downloadTemp(): void {
    const gp = this.globalService.global?.globalParams || {};
    const screenkey = this.activeScreen.screen_key;
    const activityKey = this.activeScreen.activity_key;
    const scenario = gp.scenario_v1 || gp.scenario;
    const clientKey = sessionStorage.getItem('client_key') || '';

    const downloadUrl =
      `${environment.base_url}/downloadTemplate?screenkey=${screenkey}` +
      `&client_key=${clientKey}` +
      `&tax_year=${gp.tax_year}` +
      `&scenario=${scenario}` +
      `&activityKey=${activityKey}`;

    const auth = sessionStorage.getItem('Authorization') || '';

    fetch(downloadUrl, {
      method: 'GET',
      headers: { Authorization: auth },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const gridName =
          this.activeScreen.data?.settings?.grid_name?.replace(/\./g, '') ||
          'template';
        link.download = gridName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        this.gtwSnackbarService.openSnackBar(
          'Error downloading template. Please try again.',
          'gtw-snackbar--red'
        );
      });
  }

  uploadTemp(): void {
    const config = new MatDialogConfig();
    config.width = '95vw';
    config.maxWidth = '95vw';
    config.position = { top: '14px' };
    config.data = {
      screenKey: this.activeScreen.screen_key,
      activityKey: this.activeScreen.activity_key,
      screenType: this.screenType,
      title: 'Existing Batch Transactions',
    };

    const dialogRef = this.openImportDialog(ImportUploadDialogComponent, config);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.success) {
        this.handleUploadSuccess(result);
      }
    });
  }

  pasteData(): void {
    const config = new MatDialogConfig();
    config.width = '320px';
    config.maxWidth = '320px';
    config.position = { top: '30px' };
    config.data = {
      screenKey: this.activeScreen.screen_key,
      title: 'Existing Batch Transactions',
    };

    const dialogRef = this.openImportDialog(ImportPasteDialogComponent, config);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.pastedData) {
        this.processPaste(result.pastedData);
      }
    });
  }

  openBatchList(): void {
    const config = new MatDialogConfig();
    config.width = '80vw';
    config.data = {
      screenKey: this.activeScreen.screen_key,
      activityKey: this.activeScreen.activity_key,
    };
    const dialogRef = this.openImportDialog(BatchProcessListDialogComponent, config);
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.loaded && result.batchData) {
        this.handleBatchLoad(result.batchData);
      }
    });
  }

  private handleBatchLoad(batchData: any): void {
    if (!batchData?.data || batchData.data.length === 0) return;

    if (!this.activeScreen.data) {
      this.activeScreen.data = {};
    }
    const gridData = this.activeScreen.data;

    this.deleteImportHeaders(gridData);
    gridData.data = batchData.data;
    this.ensureImportRowObjectIds(gridData);
    this.cacheImportRowEnrichmentTemplate(batchData.data);

    this.tagNumber = '';
    this.batchTags = batchData.tagNo || [];

    this.addImportHeaders(gridData);
    this.normalizeImportEditableHeaders(gridData);
    this.transposeData(gridData);

    gridData.firstLoad = null;
    gridData.callSuccess = '1';
    this.activeScreen.data = gridData;

    this.importObj.step = 2;
    this.importObj.dirty = true;
    this.importObj.saving = false;
    this.importObj.times_submitted = 0;

    this.workspaceService.dataFetched.next(true);
    this.gridEventService.broadcast('dataLoaded', gridData);
    this.gridEventService.broadcast('showFilterGroup', { showFilter: true });
    this.cdr.detectChanges();
  }

  handleUploadSuccess(result: any): void {
    const response = result.data;
    const uploadedFileName = String(result?.fileName || '');
    if (!response?.data || response.data.length === 0) return;

    const gridData = this.activeScreen.data || {};

    // Defensive: re-uploading a file on the same screen without first clicking
    // Cancel must not leave stale `error_msg`/`validated` headers from the
    // previous attempt. `addImportHeaders` short-circuits when those headers
    // already exist, so we clear them here before the new dataset is loaded —
    // mirrors how the legacy flow reaches `pasteExport.loadGrid` only after
    // the Cancel path has run `deleteImportHeaders`.
    this.deleteImportHeaders(gridData);

    gridData.data = response.data;
    this.ensureImportRowObjectIds(gridData);
    this.cacheImportRowEnrichmentTemplate(response.data);

    this.tagNumber = '';
    this.batchTags = response.tagNo || [];

    if (response.raw_data) {
      this.globalService.setRawData(response.raw_data);
    }
    if (response.feed_file) { this.feedFile = response.feed_file; }
    if (response.trans_file) { this.transFile = response.trans_file; }

    this.importObj.step = 2;
    this.importObj.dirty = true;
    this.importObj.saving = false;
    this.importObj.times_submitted = 0;

    // Legacy `DataFiltersController.js` `fileSuccess`: Sch P / insurance path uses a
    // richer `showFilterGroup` payload and chains `getSchPGridDataFromTemplate` with
    // the same `name=` encoding as `getGridDataFromTemplate`.
    const rows = response.data;
    if (Array.isArray(rows) && rows.length > 1 && rows[1]?.EIN) {
      this.gridEventService.broadcast('showFilterGroup', {
        showFilter: true,
        einNo: rows[1].EIN,
        chart_type: rows[1].CHART_TYPE,
        feed_file: response.feed_file,
        tax_year: rows[1].TAX_YEAR,
        trans_file: response.trans_file,
      });
      this.maybeFetchSchPGridFromTemplate(uploadedFileName);
    } else {
      this.gridEventService.broadcast('showFilterGroup', { showFilter: true });
    }

    if (response.errorList?.length > 0) {
      this.applyImportErrors(gridData, response.errorList);
    }

    this.addImportHeaders(gridData);
    this.normalizeImportEditableHeaders(gridData);
    this.transposeData(gridData);

    gridData.firstLoad = null;
    gridData.callSuccess = '1';
    this.activeScreen.data = gridData;

    this.workspaceService.dataFetched.next(true);
    this.gridEventService.broadcast('dataLoaded', gridData);
    // Parity with AngularJS `$rootScope.$broadcast('templateLoaded', response)` after upload.
    this.gridEventService.broadcast('templateLoaded', response);
    this.cdr.detectChanges();
  }

  /**
   * Legacy `DataFiltersController.js` `fileSuccess` → `getSchPGridDataFromTemplate` (same `name=` as main template call).
   */
  private maybeFetchSchPGridFromTemplate(fileName: string): void {
    if (!fileName) return;
    const nameQs = encodeURIComponent(fileName).replace(/%20/g, '+');
    const url = `${environment.base_url}/getSchPGridDataFromTemplate?name=${nameQs}`;
    this.http.get<any>(url, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (pResponse) => {
        if (pResponse?.data === undefined) {
          this.gtwSnackbarService.openSnackBar(
            'Empty File Can not be uploaded! Please make sure file has Data',
            'gtw-snackbar--yellow'
          );
          this.gridEventService.broadcast('showFilterGroup', { showFilter: false });
          return;
        }
        if (pResponse?.raw_data?.length > 1 && pResponse.raw_data) {
          this.globalService.setpRawData(pResponse.raw_data);
        }
      },
      (err) => {
        const msg = err?.error?.errorMessage || 'Failed to load supplemental grid data.';
        this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
      }
    );
  }

  /**
   * Paste: parse clipboard TSV locally (legacy parity — grid shows immediately).
   * Do not upload a synthetic xlsx; the backend rejects that path with 406.
   */
  private processPaste(pastedText: string): void {
    this.processXls(pastedText);
    // Do not call enrichPasteViaXlsxUpload — synthetic xlsx is rejected by backend (406).
  }

  private cacheImportRowEnrichmentTemplate(rows: any[]): void {
    const sample = rows?.find((row) => row?.groupObjKey != null);
    if (sample) {
      this.importRowEnrichmentTemplate = _.cloneDeep(sample);
    }
  }

  private enrichImportRowsForSave(rows: any[]): any[] {
    const template = this.importRowEnrichmentTemplate;
    if (!template) {
      return rows;
    }

    return rows.map((row) => {
      if (row?.groupObjKey != null) {
        return row;
      }
      return { ..._.cloneDeep(template), ...row };
    });
  }

  private buildXlsxFileFromPaste(pastedText: string, gridData: any): Promise<File | null> {
    const headerArray = _.filter(gridData?.header || [], { display: 'y' });
    if (headerArray.length === 0) {
      return Promise.resolve(null);
    }

    const cleaned = pastedText.replace(/\r/g, '');
    const lines = cleaned.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) {
      return Promise.resolve(null);
    }

    const objectIdHeader = headerArray.find(
      (header: any) => String(header?.map || '').toLowerCase() === 'object_id'
    );
    const hasVisibleObjectIdColumn = !!objectIdHeader && headerArray[0] === objectIdHeader;
    const headerRow = headerArray.map((header: any) => header.label || header.map || '');

    const dataRows = lines.map((line, rowIndex) => {
      const cells = line.split('\t');
      const row: string[] = [];
      let cellIndex = 0;

      headerArray.forEach((header: any) => {
        const map = String(header?.map || '').toLowerCase();
        if (map === 'object_id' && hasVisibleObjectIdColumn) {
          row.push(String(rowIndex + 1));
          return;
        }

        row.push((cells[cellIndex] ?? '').trim());
        cellIndex++;
      });

      return row;
    });

    const sheetData = [headerRow, ...dataRows];
    const fileName = `clipboard_paste_${this.activeScreen.screen_key || 'import'}_${Date.now()}.xlsx`;
    return aoaToXlsxFile(sheetData, fileName, 'Sheet1');
  }

  /** Background enrichment — does not block local paste display. */
  private enrichPasteViaXlsxUpload(pastedText: string): void {
    const gridData = this.activeScreen?.data;
    if (!gridData?.header) {
      return;
    }

    this.buildXlsxFileFromPaste(pastedText, gridData).then((file) => {
    if (!file) {
      return;
    }

    this.uploadTemplateFile(file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (body) => {
        if (body?.uploadStatus === 'error') {
          return;
        }

        const uploadFileName =
          body?.uploadStatus === 'success' ? body.flowFilename : file.name;
        if (!uploadFileName) {
          return;
        }

        const nameQs = GridImportToolbarComponent.buildTemplateNameQueryString(uploadFileName);
        const url = `${environment.base_url}/getGridDataFromTemplate?name=${nameQs}`;

        this.http.get<any>(url, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response) => {
            if (!response?.data?.length) {
              return;
            }

            this.deleteImportHeaders(gridData);
            gridData.data = response.data;
            this.ensureImportRowObjectIds(gridData);
            this.cacheImportRowEnrichmentTemplate(response.data);

            if (response.tagNo) {
              this.batchTags = response.tagNo;
            }

            if (response.errorList?.length > 0) {
              this.applyImportErrors(gridData, response.errorList);
            }

            this.addImportHeaders(gridData);
            this.normalizeImportEditableHeaders(gridData);
            this.transposeData(gridData);
            gridData.callSuccess = '1';
            this.activeScreen.data = gridData;
            this.gridEventService.broadcast('dataLoaded', gridData);
            this.cdr.detectChanges();
          },
          error: () => {
            // Local paste data remains visible; save may use cached template merge.
          },
        });
      },
      error: () => {
        // Local paste data remains visible.
      },
    });
    }).catch(() => {
      // Local paste data remains visible.
    });
  }

  private static buildTemplateNameQueryString(fileName: string): string {
    return encodeURIComponent(fileName).replace(/%20/g, '+');
  }

  private uploadTemplateFile(file: File) {
    const chunkSize = 104857600;
    const flowIdentifier = file.size + '-' + file.name.replace(/[^a-zA-Z0-9]/g, '');
    const formData = new FormData();
    formData.append('flowChunkNumber', '1');
    formData.append('flowChunkSize', String(chunkSize));
    formData.append('flowCurrentChunkSize', String(file.size));
    formData.append('flowTotalSize', String(file.size));
    formData.append('flowIdentifier', flowIdentifier);
    formData.append('flowFilename', file.name);
    formData.append('flowRelativePath', file.name);
    formData.append('flowTotalChunks', '1');
    formData.append('file', file, file.name);

    return this.http.post<any>(`${environment.base_url}/uploadTemplate`, formData, {
      withCredentials: true,
    });
  }

  private processXls(text: string): void {
    const cleaned = text.replace(/\r/g, '');
    const rows = cleaned.split('\n');
    const cols = cleaned.split('\t');
    const gridData = this.activeScreen.data;
    if (!gridData?.header) return;

    // Clear import-only columns before deriving the visible header mapping.
    // Otherwise a paste after a failed validation can map the first pasted
    // value into stale `error_msg` / `validated` columns and shift the real
    // fields, which makes the grid appear blank or incorrectly populated.
    this.deleteImportHeaders(gridData);
    const headerArray = _.filter(gridData.header, { display: 'y' });
    const objectIdHeader = headerArray.find((header: any) => String(header?.map || '').toLowerCase() === 'object_id');
    const hasVisibleObjectIdColumn = !!objectIdHeader && headerArray[0] === objectIdHeader;

    gridData.data = [];

    if (rows.length && cols.length >= 2) {
      for (let i = 0; i < rows.length; i++) {
        if (rows[i] !== '' && typeof rows[i] !== 'undefined') {
          const newRow: any = {};
          const cells = rows[i].split('\t');
          newRow.object_id = (i + 1).toString();
          if (hasVisibleObjectIdColumn) {
            newRow[objectIdHeader.map] = newRow.object_id;
          }
          for (let c = 0; c < cells.length; c++) {
            const hI = hasVisibleObjectIdColumn ? c + 1 : c;
            if (headerArray[hI] !== undefined) {
              const cellVal = cells[c].trim();
              if (headerArray[hI].validation) {
                newRow[headerArray[hI].map] = this.validateCell(cellVal, headerArray[hI].validation);
              } else {
                newRow[headerArray[hI].map] = cellVal;
              }
            }
          }
          gridData.data.push(newRow);
        }
      }
      this.ensureImportRowObjectIds(gridData);
      this.loadPastedGrid(gridData);
      this.gridEventService.broadcast('showFilterGroup', { showFilter: true });
    } else {
      this.gtwSnackbarService.openSnackBar(
        'The data you have pasted from the clip board does not appear to be data copied from an XLS file.',
        'gtw-snackbar--yellow'
      );
    }
  }

  private normalizeImportRowsForSave(rows: any[], headers: any[]): void {
    _.forEach(rows, (row: any) => {
      _.forEach(headers, (header: any) => {
        const map = header?.map;
        if (!map || row[map] === undefined || row[map] === null || row[map] === '') {
          return;
        }

        const validation = String(header.validation || '').toLowerCase();
        const dataType = String(header.data_type || '').toLowerCase();
        const shouldNormalize =
          validation === 'numeric' || dataType === 'number' || header.round_off === '1';

        if (!shouldNormalize) {
          return;
        }

        const normalized = this.normalizeNumeric(String(row[map]));
        if (normalized !== null) {
          row[map] = header.round_off === '1' ? Math.round(normalized) : normalized;
        }
      });
    });
  }

  private validateCell(value: string, validation: string): any {
    if (validation === 'numeric') {
      const parsed = this.normalizeNumeric(value);
      return parsed === null ? '' : parsed;
    } else if (validation === 'date') {
      return '';
    }
    return value;
  }

  // Normalizes Excel-style numeric strings into a JS number, preserving negatives.
  // Handles accounting parentheses "(1,000)" -> -1000, Unicode minus (U+2212),
  // currency symbols, and thousands separators. Returns null if not parseable.
  private normalizeNumeric(value: string): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    let str = String(value).trim();
    if (str === '') {
      return null;
    }
    str = str.replace(/\u2212/g, '-');
    let negative = false;
    const parenMatch = str.match(/^\((.*)\)$/);
    if (parenMatch) {
      negative = true;
      str = parenMatch[1].trim();
    }
    str = str.replace(/[^0-9.,\-]/g, '');
    str = str.replace(/,/g, '').replace(/\.$/, '');
    if (str.indexOf('-') > 0) {
      str = '-' + str.replace(/-/g, '');
    }
    if (str === '' || str === '-' || isNaN(Number(str))) {
      return null;
    }
    let num = Number(str);
    if (negative && num > 0) {
      num = -num;
    }
    return num;
  }

  private loadPastedGrid(gridData: any): void {
    if (gridData.data === undefined) {
      this.importObj.step = 1;
    } else {
      this.addImportHeaders(gridData);
      this.normalizeImportEditableHeaders(gridData);
      this.importObj.step = 2;
      this.importObj.dirty = true;
      this.importObj.times_submitted = 0;
      this.importObj.crudLoading = false;
      this.importObj.cancelcrudLoading = false;
      this.transposeData(gridData);

      gridData.firstLoad = null;
      gridData.callSuccess = '1';
      this.activeScreen.data = gridData;

      this.workspaceService.dataFetched.next(true);
      this.gridEventService.broadcast('dataLoaded', gridData);
    }
    this.cdr.detectChanges();
  }

  private ensureImportRowObjectIds(gridData: any): void {
    if (!Array.isArray(gridData?.data)) return;

    gridData.data.forEach((row: any, index: number) => {
      if (!row) return;

      if (row.object_id === undefined || row.object_id === null || row.object_id === '') {
        row.object_id = (index + 1).toString();
      } else {
        row.object_id = String(row.object_id);
      }
    });
  }

  private normalizeImportEditableHeaders(gridData: any): void {
    if (!Array.isArray(gridData?.header)) return;

    gridData.header.forEach((header: any) => {
      const map = String(header?.map || '').toLowerCase();
      if (map === 'object_id' || map === 'error_msg' || map === 'validated') {
        header.editable = 'n';
        return;
      }

      if (this.isEditableFlag(header?.editable) || this.isEditableFlag(header?.new_row_editable)) {
        header.editable = 'y';
        header.edit_type = header.edit_type || 'text';
      }
    });
  }

  private isEditableFlag(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1';
  }

  private deleteImportHeaders(gridData: any): void {
    if (!gridData?.header) return;
    const errorIdx = _.findIndex(gridData.header, { map: 'error_msg' });
    if (errorIdx > -1) {
      gridData.header.splice(errorIdx, 1);
    }
    const validatedIdx = _.findIndex(gridData.header, { map: 'validated' });
    if (validatedIdx > -1) {
      gridData.header.splice(validatedIdx, 1);
    }
  }

  private addImportHeaders(gridData: any): void {
    if (!gridData.header) return;
    const hasError = gridData.header.some((h: any) => h.map === 'error_msg');
    if (hasError) return;

    const errorHeader: any = {
      call_back: null, col_class: '', data_type: 'string', display: 'y',
      drilldown: null, edit_type: 'text', editable: 'n', format: null,
      full_label: null, grid_header_key: '168', grid_key: '10',
      header_class: null, label: 'Error Message', map: 'error_msg',
      object_id: 'error_msg', priority: 2, validation: null, width: 170,
    };
    const validatedHeader: any = {
      call_back: null, col_class: '', data_type: 'string', display: 'y',
      drilldown: null, edit_type: 'text', editable: 'n', format: null,
      full_label: null, grid_header_key: '168', grid_key: '10',
      header_class: null, label: 'Validated', map: 'validated',
      object_id: 'validated', priority: 2, validation: null, width: 100,
    };

    gridData.header.splice(1, 0, errorHeader);
    gridData.header.push(validatedHeader);
  }

  private transposeData(gridData: any): void {
    if (!gridData.header || !gridData.data) return;
    const transposeMaps: any[] = [];
    _.forEach(gridData.header, (header: any) => {
      if (header.transpose) {
        transposeMaps.push({ [header.map]: header.transpose });
      }
    });
    _.forEach(gridData.data, (row: any) => {
      _.forEach(transposeMaps, (map: any) => {
        _.forEach(map, (value: string, key: string) => {
          if (row[key]) {
            row[key] = value === 'uppercase' ? row[key].toUpperCase() : row[key].toLowerCase();
          }
        });
      });
    });
  }

  private applyImportErrors(gridData: any, errorList: any[]): void {
    if (!gridData.data) return;
    _.forEach(gridData.data, (row: any) => {
      const match = _.find(errorList, (e: any) => e.object_id === row.object_id);
      if (match) {
        row.error_msg = match.error_message;
        row.row_class = 'gg-error-row';
        row.col_class = 'gg-error-col';
        row.validated = 'false';
        this.importObj.errorLength = errorList.length;
      } else {
        delete row.error_msg;
        row.row_class = null;
        row.validated = 'true';
      }
    });
    if (errorList.length > 0) {
      gridData.tableState = gridData.tableState || { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      gridData.tableState.sort = {
        predicate: 'error_msg',
        lable: 'Error Message',
        reverse: false,
      };
      this.gtwSnackbarService.openSnackBar(
        'Please check below batch import errors.',
        'gtw-snackbar--red'
      );
    }
  }

  private getAllImportRowsForSave(gridData: any): any[] {
    const fullRows: any[] = _.cloneDeep(gridData?.data || []);
    const workingRows: any[] = _.cloneDeep(gridData?.dataArr || []);

    if (fullRows.length === 0) {
      return workingRows;
    }

    if (workingRows.length === 0) {
      return fullRows;
    }

    const fullRowIndexes = new Map<string, number>();
    fullRows.forEach((row: any, index: number) => {
      if (row?.object_id != null) {
        fullRowIndexes.set(String(row.object_id), index);
      }
    });

    // Overlay the grid working row values onto the full dataset. This keeps
    // inline edits from the currently rendered grid while preserving all
    // untouched rows that are only present in the canonical import data.
    workingRows.forEach((row: any) => {
      const objectId = row?.object_id != null ? String(row.object_id) : '';
      const fullIndex = fullRowIndexes.get(objectId);
      if (fullIndex != null) {
        fullRows[fullIndex] = { ...fullRows[fullIndex], ...row };
      }
    });

    return fullRows;
  }

  cancel(): void {
    if (
      this.activeScreen &&
      GridImportToolbarComponent.isInsuranceCancelId(this.activeScreen.id)
    ) {
      this.showSaveBtn = true;
    }

    this.tagNumber = '';
    this.batchTags = [];
    this.importRowEnrichmentTemplate = null;

    if (this.importObj.cancelcrudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    this.importObj.cancelcrudLoading = true;
    this.importObj.errorLength = null;
    this.globalService.setBslaFilterState(false);
    this.globalService.setRawData([]);
    this.importObj.step = 1;

    const gridData = this.activeScreen.data;
    if (gridData) {
      gridData.data = [{ object_id: '1' }];
      if (gridData.dataArr) {
        gridData.dataArr = [{ object_id: '1' }];
      }
      this.deleteImportHeaders(gridData);
    }
    this.importObj.records = 0;
    this.gridEventService.broadcast('dataLoaded', gridData);
    this.gridEventService.broadcast('showFilterGroup', { showFilter: false });
    this.importObj.cancelcrudLoading = false;
    this.activeScreen.saving_data = false;
    delete this.activeScreen.filters;
    this.cdr.detectChanges();
  }

  openCancelDataLoadConfirmation(): void {
    this.showCancelConfirmModal = true;
  }

  onCancelDataLoadConfirmYes(): void {
    this.showCancelConfirmModal = false;
    this.cancel();
  }

  onCancelDataLoadConfirmNo(): void {
    this.showCancelConfirmModal = false;
  }

  save(): void {
    this.importObj.submitted = true;
    this.activeScreen.saving_data = true;

    if (this.importObj.crudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    this.importObj.crudLoading = true;
    this.importObj.cancelcrudLoading = true;

    const gridData = this.activeScreen.data;
    if (!gridData) {
      this.gtwSnackbarService.openSnackBar(
        'No data to save.',
        'gtw-snackbar--red'
      );
      this.resetSaveState();
      return;
    }

    const routeConfig = this.appService.ROUTES?.[this.activeScreen.link];
    const saveUrl = routeConfig?.api?.save;
    if (!saveUrl) {
      this.gtwSnackbarService.openSnackBar(
        'Save call is not set in the screen. Please contact support.',
        'gtw-snackbar--red'
      );
      this.resetSaveState();
      return;
    }

    let fullUrl = saveUrl;
    if (fullUrl.startsWith('http')) {
      // absolute URL, use as-is
    } else if (fullUrl.includes('gridBatchImportValidation')) {
      fullUrl = environment.batch_api_url + (fullUrl.startsWith('/') ? '' : '/') + fullUrl;
    } else if (fullUrl.includes('gridInsBatchImportValidation')) {
      fullUrl = environment.batch_api_url + (fullUrl.startsWith('/') ? '' : '/') + fullUrl;
    } else if (!fullUrl.startsWith(environment.base_url)) {
      fullUrl = environment.base_url + (fullUrl.startsWith('/') ? '' : '/') + fullUrl;
    }

    // Import save is not regular grid inline-save. The backend expects the
    // entire import grid in clob_data on every validation call, even if the
    // user edited only one row inline. `dataArr` is gizmoGrid's working array
    // and can represent the filtered/edited viewport; `data` is the canonical
    // full import dataset loaded from upload / paste / saved batch. Start from
    // `data`, then overlay any current `dataArr` row values by object_id so
    // inline edits made in the visible grid are preserved without dropping
    // untouched rows.
    const dataRows = this.getAllImportRowsForSave(gridData);

    _.forEach(gridData.header, (header: unknown) => {
      const typedHeader = header as { map?: string; validation?: string; data_type?: string; round_off?: string };
      const map = typedHeader?.map;
      if (!map) {
        return;
      }
      const isNumeric =
        String(typedHeader.validation || '').toLowerCase() === 'numeric' ||
        String(typedHeader.data_type || '').toLowerCase() === 'number' ||
        typedHeader.round_off === '1';
      if (!isNumeric) {
        return;
      }
      _.forEach(dataRows, (row: Record<string, unknown>) => {
        if (row[map] === '' || row[map] == null) {
          row[map] = 0;
        }
      });
    });

    const roundOffMaps: string[] = [];
    _.forEach(gridData.header, (header: any) => {
      if (header.round_off === '1') {
        roundOffMaps.push(header.map);
      }
    });

    _.forEach(dataRows, (row: any) => {
      delete row.validated;
      delete row.col_class;
      row.Error = '';
      row.Validated = '';
      _.forEach(roundOffMaps, (key: string) => {
        const v = row[key];
        if (v !== '' && v != null && !Number.isNaN(Number(v))) {
          row[key] = Math.round(Number(v));
        }
      });
    });
    this.normalizeImportRowsForSave(dataRows, gridData.header || []);

    const filterParams = this.activeScreen.filters?.getFilterParams?.() || {};
    const gp = this.globalService.global?.globalParams || {};

    // Legacy parity: `DataFiltersController.js` save (~L1276–1285) does
    // `params = filterParams` then `_.merge({}, GlobalService.globalParams, params)`
    // so tax_year, screen_key, scenario, jcd_*, scenarioCode, is_issue_key_enabled,
    // return_type, submission_id, form_name, action_id (when present on global),
    // checkLockedbj, etc. all flow to `gridBatchImportValidation` (e.g. action_id 33121).
    let params: any = _.merge({}, gp, filterParams);

    delete params.clob_settings;
    delete params.clob_data;
    delete params.grid_key;

    params.EIN = this.globalService.getEIN() || '';
    params.tag = this.batchTags.length > 0 ? this.batchTags.join(',') : '';
    params.record_count = dataRows.length;
    params.process_name = (this.activeScreen.label || params.process_name || '').toString();

    params.activity_key = this.activeScreen.activity_key ?? params.activity_key ?? '';
    params.screen_type = this.activeScreen.screen_type || params.screen_type || '';
    params.screen_key = (this.activeScreen.screen_key || params.screen_key || gp.screen_key || '').toString();
    params.tax_year = (params.tax_year ?? gp.tax_year ?? filterParams.tax_year ?? '').toString();
    params.scenario = (params.scenario ?? gp.scenario ?? filterParams.scenario ?? '').toString();
    params.jcd_key = (params.jcd_key ?? gp.jcd_key ?? filterParams.jcd_key ?? '').toString();
    params.jcd_ta_key = (params.jcd_ta_key ?? gp.jcd_ta_key ?? filterParams.jcd_ta_key ?? '').toString();
    params.is_locked = (params.is_locked ?? gp.is_locked ?? 'N').toString();
    params.issue_id = (params.issue_id ?? gp.issue_id ?? '').toString();
    params.issue_short_desc = (params.issue_short_desc ?? gp.issue_short_desc ?? '').toString();
    params.issue_id_for_tx_save = (params.issue_id_for_tx_save ?? gp.issue_id_for_tx_save ?? '').toString();

    const actionIdFromUrl = String(fullUrl).match(/(?:[?&])action_id=(\d+)/);
    if (actionIdFromUrl?.[1]) {
      params.action_id = actionIdFromUrl[1];
    }
    const batchSaveKey =
      (this.activeScreen as any).batch_save_action_key ??
      this.activeScreen?.data?.settings?.batch_save_action_key;
    if (batchSaveKey != null && batchSaveKey !== '' && (params.action_id === undefined || params.action_id === '')) {
      params.action_id = String(batchSaveKey);
    }

    params.scenarioCode = (params.scenarioCode || gp.scenarioCode || 'RAF').toString();
    params.is_issue_key_enabled = (params.is_issue_key_enabled ?? gp.is_issue_key_enabled ?? 'N').toString();

    const checkLockedbj: any[] = [];
    const selectedRowData = gridData.selected?.rowData;
    if (_.isArray(selectedRowData) && selectedRowData.length > 0) {
      _.each(selectedRowData, (item: any) => {
        const d = item.data || item;
        checkLockedbj.push({
          leid: d.LEID || d.SOURCE_LEID,
          me_code: d.ME_CODE || d.ME || d.SOURCE_ME_CODE,
          cdr_number: d.CDR_NO || d.TAX_CODE || d.SOURCE_CDR_NO,
          consol_group_key: d.CONSOL_GROUP_KEY || d.FILING_GROUP,
          reporting_period: d.REPORTING_PERIOD || d.SOURCE_REPORTING_PERIOD,
          combination_key: d.COMBINATION_KEY || d.COMPL_COMBINATION_KEY || d.HO_COMBINATION_KEY,
          scenario_key: String(d.SCENARIO_KEY ?? params.scenario ?? ''),
        });
      });
    } else if (selectedRowData) {
      checkLockedbj.push({
        leid: selectedRowData.LEID || selectedRowData.SOURCE_LEID,
        me_code: selectedRowData.ME_CODE || selectedRowData.ME || selectedRowData.SOURCE_ME_CODE,
        cdr_number: selectedRowData.CDR_NO || selectedRowData.TAX_CODE || selectedRowData.SOURCE_CDR_NO,
        consol_group_key: selectedRowData.CONSOL_GROUP_KEY || selectedRowData.FILING_GROUP,
        reporting_period: selectedRowData.REPORTING_PERIOD || selectedRowData.SOURCE_REPORTING_PERIOD,
        combination_key:
          selectedRowData.COMBINATION_KEY ||
          selectedRowData.COMPL_COMBINATION_KEY ||
          selectedRowData.HO_COMBINATION_KEY,
        scenario_key: String(selectedRowData.SCENARIO_KEY ?? params.scenario ?? ''),
      });
    } else {
      checkLockedbj.push({ scenario_key: String(params.scenario ?? '') });
    }
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Snapshot for clob_settings (legacy AngularJS / backend parity): omit fields
    // that are not duplicated at top level, and use placeholder `[{}]` for
    // checkLockedbj inside clob_settings only — real lock context stays on
    // top-level `checkLockedbj`.
    const clobForSave = _.cloneDeep(params);
    delete clobForSave.scenario_v1;
    delete clobForSave.scenario_desc;
    delete clobForSave.entityType;
    delete clobForSave.action_id;
    clobForSave.checkLockedbj = JSON.stringify([{}]);
    params.clob_settings = JSON.stringify(clobForSave);

    delete params.scenario_v1;
    delete params.scenario_desc;
    delete params.entityType;
    delete params.action_id;

    const rowsForSave = _.cloneDeep(dataRows);
    _.forEach(rowsForSave, (row: any) => {
      delete row.error_msg;
      delete row.row_class;
      delete row.col_class;
      delete row.validated;
    });

    // Unlike normal grid inline edit, batch import validation must always send
    // every row currently present in the import grid. Legacy AngularJS sent
    // `prepareJsonForSave(gridData.data, ...)`; for this migrated flow the
    // full-row set is already normalized in `rowsForSave`, including any
    // visible inline edits overlaid from `dataArr`.
    params.clob_data = JSON.stringify(rowsForSave);

    params.grid_key = gridData.settings?.grid_key || '';

    if (fullUrl.includes('gridInsBatchImportValidation')) {
      params.clob_data = JSON.stringify(dataRows);
      const clobSettings = JSON.parse(params.clob_settings);
      clobSettings.feed_file = this.feedFile;
      clobSettings.trans_file = this.transFile;
      params.clob_settings = JSON.stringify(clobSettings);
    }

    this.gridEventService.broadcast('showLoader', true);

    // Use URLSearchParams instead of Angular HttpParams for this form-encoded
    // payload. HttpParams' default codec leaves literal `+` characters in
    // values, and application/x-www-form-urlencoded decoders read `+` as a
    // space. That corrupts clob_data text like "ending (+)" before it reaches
    // /gridBatchImportValidation. URLSearchParams encodes literal plus as
    // `%2B`, while spaces become `+`, matching form-urlencoded semantics.
    const body = new URLSearchParams();
    for (const key of Object.keys(params)) {
      if (params[key] != null) {
        body.set(key, String(params[key]));
      }
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    this.http.post<any>(fullUrl, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.gridEventService.broadcast('showLoader', false);

        if (res?.callSuccess === '1') {
          this.importObj.submitted = false;
          this.importObj.saving = false;
          this.activeScreen.saving_data = false;

          const errorArray = res.errorList || [];
          let errors = false;

          if (errorArray.length > 0) {
            const errorObject: any = {};
            _.forEach(errorArray, (v: any) => {
              _.forEach(v.error_columns, (v1: any, k1: string) => {
                errorObject[k1] = v1;
              });
            });
            gridData.errorObject = errorObject;
          }

          _.forEach(gridData.data, (row: any) => {
            const idx = _.findIndex(errorArray, (o: any) => o.object_id === row.object_id);
            if (idx > -1) {
              row.error_msg = errorArray[idx].error_message;
              this.importObj.errorLength = errorArray.length;
              row.row_class = 'gg-error-row';
              row.col_class = 'gg-error-col';
              row.validated = 'false';
              errors = true;
            } else {
              delete row.error_msg;
              row.row_class = null;
              row.validated = 'true';
            }
          });

          gridData.tableState = gridData.tableState || { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
          gridData.tableState.sort = { predicate: 'error_msg' };

          if (errors) {
            this.gtwSnackbarService.openSnackBar(
              'Batch Import could not be saved, please check the errors below.',
              'gtw-snackbar--red'
            );
            this.importObj.times_submitted++;
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Batch Import was successfully saved.',
              'gtw-snackbar--green'
            );
            this.importObj.records = gridData.data?.length || 0;
            this.importObj.step = 3;
          }

          this.importObj.crudLoading = false;
          this.importObj.cancelcrudLoading = false;
          this.gridEventService.broadcast('dataUpdate', gridData);
        } else {
          this.handleSaveError(res);
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.gridEventService.broadcast('showLoader', false);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red'
        );
        this.resetSaveState();
        this.cdr.detectChanges();
      }
    );
  }

  private handleSaveError(res: any): void {
    const errorMsg = res?.errorMessage || 'Save failed. Please try again.';
    this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
    this.resetSaveState();
  }

  private resetSaveState(): void {
    this.importObj.saving = false;
    this.importObj.crudLoading = false;
    this.importObj.cancelcrudLoading = false;
    this.importObj.times_submitted = 0;
    this.activeScreen.saving_data = false;
  }

  addBatchTag(tag: string): void {
    if (!tag?.trim()) return;
    if (this.batchTags.length < 5) {
      this.batchTags.push(tag.trim());
      this.tagNumber = '';
    } else {
      this.gtwSnackbarService.openSnackBar(
        'Cannot exceed more than 5 tags.',
        'gtw-snackbar--red'
      );
    }
  }

  removeBatchTag(index: number): void {
    this.batchTags.splice(index, 1);
  }

  openDocument(): void {
    if (!this.activeScreen?.screen_key) return;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '80vw';
    dialogConfig.data = {
      screen_key: this.activeScreen.screen_key,
      client_key: sessionStorage.getItem('client_key') || '',
      screenName: this.activeScreen.label || this.activeScreen.screen_name || '',
      baseUrl: environment.admin_api_url || '/admin',
    };
    this.openImportDialog(ScreenDocumentsComponent, dialogConfig);
  }

  /**
   * gizmoGrid keeps keyboard focus on `.gg-body` (tabindex=1). When MatDialog
   * opens it sets aria-hidden on app root while that node stays focused, which
   * triggers the browser accessibility warning. Release grid focus and move
   * focus into the dialog pane after it opens.
   */
  private openImportDialog(component: any, config: MatDialogConfig): MatDialogRef<any> {
    this.releaseGridFocusBeforeDialog();
    config.autoFocus = config.autoFocus ?? 'first-tabbable';
    config.restoreFocus = config.restoreFocus ?? true;

    const dialogRef = this.dialog.open(component, config);
    dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
      this.ensureDialogReceivesFocus();
    });
    return dialogRef;
  }

  private releaseGridFocusBeforeDialog(): void {
    const active = document.activeElement as HTMLElement | null;
    if (!active) {
      return;
    }

    const focusedInsideGrid =
      active.classList.contains('gg-body') ||
      active.classList.contains('gg-hidden-textarea') ||
      !!active.closest('.gg-grid');

    if (focusedInsideGrid) {
      active.blur();
    }
  }

  private ensureDialogReceivesFocus(): void {
    this.releaseGridFocusBeforeDialog();

    const dialogContainer = document.querySelector(
      '.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-dialog-container'
    ) as HTMLElement | null;

    if (dialogContainer && !dialogContainer.contains(document.activeElement)) {
      dialogContainer.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.tabSub) {
      this.tabSub.unsubscribe();
    }
    if (this.showSaveButtonListenerFn) {
      this.gridEventService.off('showSaveButton', this.showSaveButtonListenerFn);
      this.showSaveButtonListenerFn = null;
    }
  }
}
